/**
 * Stage definitions for the UI. The numeric `stage` values (0–5) and owners mirror
 * backend/seldon_tasks/models.py and are stored in DynamoDB: never renumber them.
 * People see `num` (1–6); code, URLs and the API use `stage`.
 */
import type { IntakeSummary } from './templateIntakes'
import type { StageOwner, Task } from './types'

/** A builder question interrupts the build while the expert edits and answers. The website
 * records the open question on the task (authoring_review_id) and clears it once the answer is
 * sent, so lists need no authoring status (workflow.builder_question). */
export const hasBuilderQuestion = (task: Pick<Task, 'stage' | 'authoring_review_id' | 'lifecycle'>): boolean => task.stage <= 2
  && !!task.authoring_review_id && task.lifecycle !== 'retired'
export const needsBuilderReply = (task: Task): boolean => hasBuilderQuestion(task)
  && (task.stage === 0 || task.stage === 2)
/** A science workflow in the authoring stage whose builder asked the template's expert a question. */
export const needsScienceAnswer = (task: Task): boolean => (task.kind === 'scientific_workflow' || !!task.collection)
  && task.stage === 3 && task.lifecycle !== 'retired' && workflowAgentState(task) === 'needsExpert'
/** A science workflow whose pipeline stopped (or never ran a builder) and waits for a developer. */
export const needsScienceDeveloper = (task: Task): boolean => (task.kind === 'scientific_workflow' || !!task.collection)
  && task.stage === 3 && task.lifecycle !== 'retired' && workflowAgentState(task) === 'needsDeveloper'
export const taskDisplayStage = (task: Task): number => task.display_stage ?? (hasBuilderQuestion(task) ? 3 : task.stage)

export interface StageDef {
  /** Stored identifier, 0–5. */
  stage: number
  /** Human-facing number, 1–6. */
  num: number
  label: string
  owner: StageOwner | null
  /** One-sentence status for the expert. */
  sme: string
  /** Stage-0 status when the draft was sent back by the engineer. */
  smeRepair?: string
  /** One-sentence status for the engineer. */
  engineer: string
  /** Wording of the forward transition, shown to the stage owner. */
  forward?: { title: string; button: string }
  /** Wording of the send-back transition, shown to the stage owner. */
  sendBack?: { button: string; prompt: string }
}

export const REVIEW_STAGES: StageDef[] = [
  { stage: 0, num: 1, label: 'Task draft', owner: 'sme',
    sme: 'Prepare the task and rubric. Review the draft with the coach before execution.',
    engineer: 'The expert is writing the draft.', forward: { title: 'Ready to execute?', button: 'Start execution' } },
  { stage: 2, num: 2, label: 'Expert execution', owner: 'sme',
    sme: 'Execute the task. Upload the starting materials and final files, then submit for peer review. Recording and tracking are optional.',
    engineer: 'The expert is preparing the execution materials.',
    forward: { title: 'Ready for peer review?', button: 'Submit for peer review' } },
  { stage: 1, num: 3, label: 'Peer review', owner: 'sme',
    sme: 'Another expert checks the submitted task and execution.',
    engineer: 'The task is waiting for peer review.' },
  { stage: 3, num: 4, label: 'Task package build', owner: 'agent',
    sme: 'The builder prepares the package when environment support is available.',
    engineer: 'Follow the builder in Agents. Unsupported tasks wait for environment support.' },
  { stage: 4, num: 5, label: 'Quality check', owner: 'engineer',
    sme: 'Review the rollouts and rubric results. Submit your recommendation.',
    engineer: 'Review the package and feedback. Return it for changes or approve publication.',
    forward: { title: 'Approve publication', button: 'Approve publication' },
    sendBack: { button: 'Return to build', prompt: 'Explain what the builder must change.' } },
  { stage: 5, num: 6, label: 'Published', owner: null,
    sme: 'The task is complete.', engineer: 'Publication is complete.' },
]

/** Compatibility name for components; every stage now uses V2. */
export const STAGES = REVIEW_STAGES
export const stageDef = (n: number, _version = 2): StageDef => REVIEW_STAGES.find(s => s.stage === n) ?? REVIEW_STAGES[0]
export const stageNum = (n: number): number => stageDef(n).num

/**
 * THE SIX PIPELINE STAGES (AGENTS.md, "The six pipeline stages").
 *
 * One ordered definition for every board: the SME dashboard, the shared directory, the developer
 * pipeline and the workflow page all render these and nothing else. The numbers below are what
 * people see; they are a presentation mapping over the stored `stage` (0–5) and the stored intake
 * status, and neither is ever renumbered to match them.
 *
 * The counted unit changes at stage 4. Stages 1–3 count templates and workflow drafts, because up
 * to there a workflow is still an idea about a template. Stages 4–6 count workflows: an authoring
 * agent belongs to a workflow, a researcher agent to a template.
 */
export type PipelineUnit = 'draft' | 'template' | 'workflow'
/** Who has to act. 'agent' stages are worked by an agent; 'complete' is nobody. */
export type PipelineOwner = 'expert' | 'agent' | 'developer' | 'complete'

export interface PipelineStage {
  /** Stable URL/filter key. Not a stored stage number. */
  key: string
  /** What people see, 1–6. */
  num: number
  label: string
  owner: PipelineOwner
  /** What this stage counts. */
  unit: PipelineUnit
  /** Templates only: the stage is skipped when an expert drafts a workflow directly. */
  templatesOnly?: boolean
  /** An agent stage splits its count into running sessions and sessions that need a developer. */
  agent?: boolean
}

export const UNIT_LABEL: Record<PipelineUnit, string> = {
  draft: 'templates and workflow drafts',
  template: 'templates',
  workflow: 'workflows',
}

export const PIPELINE_STAGES: PipelineStage[] = [
  { key: 'draft', num: 1, label: 'Draft', owner: 'expert', unit: 'draft' },
  { key: 'research', num: 2, label: 'Researcher agent', owner: 'agent', unit: 'template', templatesOnly: true, agent: true },
  { key: 'workflow_review', num: 3, label: 'Peer review', owner: 'expert', unit: 'template', templatesOnly: true },
  { key: 'authoring', num: 4, label: 'Authoring agent', owner: 'agent', unit: 'workflow', agent: true },
  { key: 'quality', num: 5, label: 'Quality check', owner: 'developer', unit: 'workflow' },
  { key: 'published', num: 6, label: 'Published', owner: 'complete', unit: 'workflow' },
]
export const pipelineStage = (key: string) => PIPELINE_STAGES.find(s => s.key === key)
/** The stages a workflow itself passes through: 2 and 3 belong to the template it came from. */
export const WORKFLOW_STAGES = PIPELINE_STAGES.filter(s => !s.templatesOnly)

/**
 * Where a scientific workflow sits on the board, from its stored stage.
 *
 * Stored 0 (workflow draft) and 2 (expert execution) are both the expert writing the draft: it
 * leaves on submit, which is exactly stage 1. A scientific workflow never stores 1 — stage 1 is
 * peer review and belongs to the task flow (`REVIEW_STAGES`) — so a row that somehow carries it
 * is read as a draft rather than dropped. Submitting puts the workflow in stored 3, which is the
 * authoring agent (4), whether it came from a template or was drafted directly.
 */
export function workflowStage(task: Pick<Task, 'stage'>): string {
  if (task.stage >= 5) return 'published'
  if (task.stage === 4) return 'quality'
  if (task.stage === 3) return 'authoring'
  return 'draft'
}

/** Statuses in which the authoring service still has an agent working. Mirrors `packages.ACTIVE`. */
export const AGENT_RUNNING = new Set(['queued', 'running', 'capacity_wait', 'pending_delivery'])
export const agentRunning = (status?: string) => !!status && AGENT_RUNNING.has(status)

/**
 * Where a workflow in the authoring stage stands. A finished build moves on by itself (import, then
 * the quality check), so it counts with the running ones. The builder's question waits for the
 * template's expert. A stopped pipeline step, or a builder that is blocked or failed, needs a
 * developer. A workflow with no builder at all predates the pipeline and has no state here.
 */
export function workflowAgentState(task: PipelineTask): AgentState | null {
  if (task.science_exception) return 'needsDeveloper'
  const status = task.authoring?.status
  if (!status) return null
  if (task.authoring_review_id || status === 'blocked_expert') return 'needsExpert'
  return agentRunning(status) || status === 'succeeded' ? 'running' : 'needsDeveloper'
}

/**
 * Where a template intake sits on the board, from its stored status.
 *
 * - `draft`, `returned` — the expert is writing it: stage 1.
 * - `queued` — stage 2, waiting for a researcher agent. With the researcher enabled a submit goes
 *   straight to `in_research` (scientific/intake.submit), so an intake resting here means no
 *   researcher took it. It is waiting, not stuck, so it is counted as queued: an environment
 *   without a researcher parks every template here and none of them needs a developer.
 * - `in_research` — stage 2, running unless the sweep recorded `research_blocked`.
 * - `open`, `claimed` — proposals waiting on an expert: stage 3.
 * - `approved` — the intake leaves the board; its workflows appear from stage 4 on.
 * - `needs_developer_attention` — the legacy pre-queue status, kept until the backfill ran. It
 *   covered submission to approval and nothing moves it on its own, so it is read as stage 2
 *   needing a developer.
 */
export function intakeStage(status: IntakeSummary['status']): string | null {
  switch (status) {
    case 'draft':
    case 'returned':
      return 'draft'
    case 'queued':
    case 'in_research':
    case 'needs_developer_attention':
      return 'research'
    case 'open':
    case 'claimed':
      return 'workflow_review'
    default:
      return null
  }
}

/**
 * Stage 2 only: a researcher session that cannot move forward without a developer. `in_research`
 * with the sweep's `research_blocked` is a stuck session, and the legacy status covered everything
 * from submission to approval with nothing to move it on. A `queued` template is merely waiting
 * for a researcher, so it is neither running nor stuck.
 */
export const intakeNeedsDeveloper = (template: Pick<IntakeSummary, 'status' | 'research_blocked'>) =>
  template.status === 'needs_developer_attention' || (template.status === 'in_research' && !!template.research_blocked)

/** Stage 2 only: submitted, with no researcher agent yet to pick it up. */
export const intakeQueued = (template: Pick<IntakeSummary, 'status'>) => template.status === 'queued'

/** What a board row has to carry. The directory projects the build status and nothing else. */
export type PipelineTask = Pick<Task, 'stage'>
  & Partial<Pick<Task, 'display_stage' | 'lifecycle' | 'workflow_version' | 'authoring_review_id' | 'science_exception'>>
  & { authoring?: { status?: string } | null }
export type PipelineIntake = Pick<IntakeSummary, 'status'> & Partial<Pick<IntakeSummary, 'research_blocked'>>

export interface StageCount {
  /** Everything the stage counts: what selecting its chip filters to. */
  total: number
  /** Agent stages only: sessions running, waiting for an agent, waiting for an expert's answer, and stuck without a developer. */
  running: number
  queued: number
  needsExpert: number
  needsDeveloper: number
}

type AgentState = 'running' | 'queued' | 'needsExpert' | 'needsDeveloper'

const split = (count: StageCount, state: AgentState) => { count[state] += 1 }

/**
 * The board's numbers. Templates are counted in stages 1–3 and workflows in stages 4–6, so a
 * caller passes both lists and no row is counted twice.
 */
export function pipelineCounts(
  tasks: PipelineTask[],
  intakes: PipelineIntake[] = [],
): Record<string, StageCount> {
  const counts: Record<string, StageCount> = {}
  for (const stage of PIPELINE_STAGES) counts[stage.key] = { total: 0, running: 0, queued: 0, needsExpert: 0, needsDeveloper: 0 }
  for (const template of intakes) {
    const key = intakeStage(template.status)
    if (!key) continue  // approved: the template left the board and its workflows took over
    counts[key].total += 1
    if (pipelineStage(key)?.agent) {
      split(counts[key], intakeNeedsDeveloper(template) ? 'needsDeveloper' : intakeQueued(template) ? 'queued' : 'running')
    }
  }
  for (const task of tasks) {
    if (task.lifecycle === 'retired') continue
    // Stages 2 and 3 count templates only, so a workflow draft is counted in stage 1.
    const key = workflowStage(task)
    counts[key].total += 1
    const state = pipelineStage(key)?.agent && workflowAgentState(task)
    if (state) split(counts[key], state)
  }
  return counts
}

/** Accept the stage keys older links and pickers used; '' means no filter. */
const LEGACY_KEYS: Record<string, string> = {
  '0': 'draft', '1': 'draft', '2': 'draft', '3': 'authoring', '4': 'quality', '5': 'published',
  rollouts: 'authoring', review: 'quality', intake: 'draft', intake_review: 'workflow_review',
}
export const pipelineFilterKey = (value: string): string =>
  (pipelineStage(value) ? value : LEGACY_KEYS[value]) ?? ''
